import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import ConfirmModal from "../../../../components/ConfirmModal";
import Input from "../../../../components/Form/Input/input";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { Box } from "../../../../components/Layout";
import SaveBar from "../../../../components/SaveBar";
import { SubHeader } from "../../../../components/SubHeader";
import { useCurrentPage, withPage } from "../../../../hooks/CurrentPage";
import { FaqItem, FaqSection } from "../../../../model/site";
import { clone, deepEqual } from "../../../../utils/objects";
import ItemsTable from "../../components/ItemsTable";
import { useSection } from "../../hooks/Section";
import { navigationState } from "../Sections/navigationState";
import { FaqNavigationState } from "./faqNavigationState";
import { Container } from "./styles";

const FaqPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // sub-screens keep the page of the Keepo being edited (?page)
  const { pageId } = useCurrentPage();

  const faqNavigationState = useRef(navigationState.get() as FaqNavigationState);

  const restoreState = useCallback(
    (newly: unknown, section: FaqSection) => {
      if (faqNavigationState.current) {
        section.pageTitle = faqNavigationState.current.currentSection.pageTitle;
        section.items = faqNavigationState.current.currentSection.items;
      } else if (newly) {
        section.pageTitle = t("pages.editor.faq.header.title");
        section.items = [];
      }
      return section;
    },
    [t, faqNavigationState]
  );

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<FaqSection>("faq", restoreState);

  const [idRemoving, setIdRemoving] = useState<string>();

  const onAddClick = () => {
    goEditItemPage();
  };

  const onEditClick = (id: string) => {
    goEditItemPage(id);
  };

  const goEditItemPage = (id?: string) => {
    const state: FaqNavigationState = {
      currentSection: clone<FaqSection>(section),
      defaultSection: faqNavigationState.current?.defaultSection || clone<FaqSection>(section),
      editingFaq: id ? section.items?.find(faq => faq.id === id) : undefined,
    };

    navigationState.set(state);
    navigate(withPage("/editor/faq/item", pageId));
  };

  const onRemoveClick = (id: string) => {
    setIdRemoving(id);
  };

  const removeFaq = () => {
    const faqs = section.items?.filter(faq => faq.id !== idRemoving) || [];
    setSection({ ...section, items: faqs });
    setIdRemoving(undefined);
  };

  const hasUnsavedChangesWrapper = (): boolean => {
    return faqNavigationState?.current?.defaultSection
      ? !deepEqual(section, faqNavigationState.current.defaultSection)
      : hasUnsavedChanges();
  };

  return (
    <Container>
      <SubHeader
        title={t("pages.editor.faq.header.title")}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChangesWrapper}
      />
      {idRemoving !== undefined && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.editor.faq.actions.remove.confirmModal.title")}
          cancelTitle={t("pages.editor.faq.actions.remove.confirmModal.cancelButton")}
          confirmButtonTitle={t("pages.editor.faq.actions.remove.confirmModal.confirmButton")}
          onCancel={() => setIdRemoving(undefined)}
          onConfirm={removeFaq}
        />
      )}
      <Form>
        <Box borderBottom>
          <FormGroup>
            <Input
              id="pageTitle"
              type="text"
              value={section.pageTitle}
              onChange={e =>
                setSection({
                  ...section,
                  pageTitle: e.target.value,
                })
              }
              label={t("pages.editor.faq.form.pageTitle")}
            />
          </FormGroup>
        </Box>
        <Box>
          <ItemsTable
            onSortUpdate={items => {
              const faqs = (items as any as FaqItem[]).map(faq => ({
                question: faq.question,
                answer: faq.answer,
                id: faq.id,
              }));
              setSection({ ...section, items: faqs });
            }}
            onAddClick={onAddClick}
            onEditClick={onEditClick}
            onRemoveClick={onRemoveClick}
            items={section.items?.map(faq => ({ ...faq, title: faq.question }))}
            title={t("pages.editor.faq.form.faqs")}
            addButtonTitle={t("pages.editor.faq.form.addFaq")}
          />
        </Box>
        <SaveBar onSave={saveSection} hasUnsavedChanges={hasUnsavedChangesWrapper} />
      </Form>
    </Container>
  );
};

export default FaqPage;
